import { QueryClientProvider } from '@tanstack/react-query';
import cn from 'classnames';
import posthog from 'posthog-js';
import React, { Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFirebase } from 'react-redux-firebase';
import { useHistory, useLocation } from 'react-router-dom';

import {
  BulkSendStatusNotifier,
  CurrencySettings,
  DefaultExperienceBlueprint,
  EmergencyNotification,
  Footer,
  Header,
  Loader,
  ShippingRules,
  Sidenav,
  Snackbar,
} from '../../components';
import queryClient from '../../config/queryConfig';
import { routes } from '../../constants/routing';
import { DepartmentMenu, SuggestGiftSidebar } from '../../containers';

import CurrencyProvider from '../../contexts/CurrencyProvider';
import FeedbackProvider from '../../contexts/FeedbackProvider';
import { HeaderInfoProvider } from '../../contexts/HeaderInfo';
import { ThemeProvider } from '../../contexts/ThemeProvider';
import UserFlowManager from '../../contexts/UserFlowManager';
import useConnectWithAIExtension from '../../hooks/useConnectWithAIExtension';
import { initAppAction } from '../../store/actions/shell';
import { selectIsAuthenticated, selectIsAuthenticatedInProgress, selectUserProfile } from '../../store/selectors/auth';
import { selectIsShowContentLoader } from '../../store/selectors/shell';

import styles from './App.module.scss';

const MultiplePortalModal = React.lazy(
  () =>
    import(
      /* webpackChunkName: "modal-multiple-portal" */ '../../components/modals/MultiplePortalModal/MultiplePortalModal'
    ),
);

interface IProps {
  children: React.ReactNode | React.ReactNode[];
}

const App = ({ children }: IProps) => {
  const history = useHistory();
  const location = useLocation();
  const firebase = useFirebase();
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isShowLoader = useSelector(selectIsAuthenticatedInProgress);
  const isShowContentLoader = useSelector(selectIsShowContentLoader);
  const userProfile = useSelector(selectUserProfile);

  React.useEffect(() => {
    dispatch(initAppAction({ firebase, history, location }));
  }, []);

  // add a user to PostHog analytics
  React.useEffect(() => {
    if (isAuthenticated) {
      const { uid, email, role, displayName: name } = userProfile || {};
      posthog.identify(uid, {
        email,
        name,
        role,
      });
    }
  }, [isAuthenticated]);

  useConnectWithAIExtension();

  const isLoginRoute = location.pathname.startsWith(routes.login);
  const isCreateSendRoute = location.pathname.startsWith(routes.createSend.root);

  return (
    <ThemeProvider>
      <CurrencyProvider>
        <QueryClientProvider client={queryClient}>
          <FeedbackProvider>
            <div className={cn(styles.App, isLoginRoute && styles.appLogin)}>
              <Loader isLoading={isShowLoader} />
              <Sidenav />
              <main className={cn(styles.main)}>
                <HeaderInfoProvider>
                  {isAuthenticated && [
                    <UserFlowManager key="userflow-manager" />,
                    <Header key="header" />,
                    <EmergencyNotification key="emergency-notification" />,
                    <CurrencySettings key="currency-settings" />,
                    <ShippingRules key="shipping-rules" />,
                    <DefaultExperienceBlueprint key="default-experience-blueprint" />,
                    <BulkSendStatusNotifier key="bulk-send-status-notifier" />,
                  ]}
                  <div className={cn(styles.content)}>{children}</div>
                </HeaderInfoProvider>
                <Footer />
                <Loader isLoading={isShowContentLoader && !isCreateSendRoute} className={cn(styles.contentLoader)} />
              </main>
              {isAuthenticated && [
                <DepartmentMenu key="department-menu" />,
                <SuggestGiftSidebar key="suggest-gift-sidebar" />,
              ]}
              <Snackbar />
              <Suspense fallback={null}>
                <MultiplePortalModal />
              </Suspense>
              <div id="dynamic-root" />
            </div>
          </FeedbackProvider>
        </QueryClientProvider>
      </CurrencyProvider>
    </ThemeProvider>
  );
};

export default App;
